import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Scissors, PackagePlus, Truck, Boxes, Users, FileSpreadsheet, Scale, ShieldAlert, Menu, Moon, Sun, LogOut, Sparkles, FileText,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export const GROUPS = [
  { id: "home", label: "Home", icon: LayoutDashboard, tabs: [{ to: "/", label: "Home", id: "dashboard" }] },
  { id: "cutting", label: "Cut Cable", icon: Scissors, tabs: [{ to: "/cutting", label: "Daily Cutting", id: "cutting", perm: "cutting:create" }] },
  { id: "inward", label: "Receive Stock", icon: PackagePlus, tabs: [{ to: "/inward", label: "Stock Inward", id: "inward" }] },
  { id: "dispatch", label: "Dispatch", icon: Truck, tabs: [{ to: "/dispatch", label: "Dispatch", id: "dispatch" }, { to: "/party-pending", label: "Party Pending", id: "party-pending" }, { to: "/outward", label: "Stock Outward", id: "outward" }] },
  { id: "quotes", label: "Quotations", icon: FileText, tabs: [{ to: "/quotations", label: "Quotations", id: "quotations", perm: "quote:write" }] },
  { id: "stock", label: "Stock", icon: Boxes, tabs: [{ to: "/live-stock", label: "Live Stock", id: "live-stock" }, { to: "/drums", label: "Drums", id: "drums" }, { to: "/ledger", label: "Ledger", id: "ledger" }, { to: "/qr-stickers", label: "QR Stickers", id: "qr-stickers" }] },
  { id: "check", label: "Stock Check", icon: Scale, tabs: [{ to: "/reconciliation", label: "Stock Count", id: "reconciliation" }, { to: "/adjustments", label: "Adjustments", id: "adjustments" }] },
  { id: "ai", label: "Ask AI", icon: Sparkles, tabs: [{ to: "/ask", label: "Ask AI", id: "ask" }, { to: "/insights", label: "Smart Alerts", id: "insights" }] },
  { id: "contacts", label: "Contacts", icon: Users, tabs: [{ to: "/customers", label: "Customers", id: "customers" }, { to: "/suppliers", label: "Suppliers", id: "suppliers" }, { to: "/employees", label: "Employees", id: "employees" }] },
  { id: "reports", label: "Reports", icon: FileSpreadsheet, tabs: [{ to: "/register", label: "Daily Register", id: "register" }, { to: "/reports", label: "Reports", id: "reports" }, { to: "/email-reports", label: "Email Reports", id: "email-reports", perm: "settings:write" }, { to: "/valuation", label: "Valuation", id: "valuation", perm: "cost:view" }, { to: "/tally", label: "Tally Export", id: "tally", perm: "tally:export" }] },
  { id: "admin", label: "Admin & Settings", icon: ShieldAlert, tabs: [{ to: "/admin", label: "Users", id: "admin", perm: "users:manage" }, { to: "/products", label: "Product Master", id: "products", perm: "products:write" }, { to: "/opening-stock", label: "Opening Stock", id: "opening", perm: "opening:upload" }, { to: "/settings", label: "Settings", id: "settings" }] },
];

const ROLE_GROUPS = {
  warehouse_staff: ["home", "cutting", "inward", "stock", "check", "ai", "admin"],
  dispatch_staff: ["home", "dispatch", "stock", "ai", "admin"],
  accounts_staff: ["home", "quotes", "dispatch", "stock", "contacts", "reports", "ai", "admin"],
  read_only: ["home", "stock", "reports", "ai", "admin"],
};

const matches = (to, path) => (to === "/" ? path === "/" : path === to || path.startsWith(`${to}/`));

export function useGroups() {
  const { user, can } = useAuth();
  const allowed = ROLE_GROUPS[user?.role];
  return GROUPS.filter((g) => !allowed || allowed.includes(g.id))
    .map((g) => ({ ...g, tabs: g.tabs.filter((t) => !t.perm || can(t.perm)) }))
    .filter((g) => g.tabs.length);
}

function SideNav({ onNavigate }) {
  const groups = useGroups();
  const { pathname } = useLocation();
  return (
    <div className="flex h-full flex-col bg-[#0B1530] text-slate-200">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-red-600 grid place-items-center font-heading font-black text-white">R</div>
          <div>
            <div className="font-heading font-bold tracking-tight text-white leading-none">ROXY CABLES</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">Inventory</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {groups.map((g) => {
          const active = g.tabs.some((t) => matches(t.to, pathname));
          return (
            <NavLink key={g.id} to={g.tabs[0].to} end={g.tabs[0].to === "/"} onClick={onNavigate} data-testid={`nav-${g.id}`}
              className={cn("flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors", active ? "bg-white/10 text-white border-l-2 border-red-500" : "hover:bg-white/5 text-slate-300")}>
              <g.icon className="h-4 w-4 shrink-0" /><span className="flex-1 truncate">{g.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="px-5 py-3 text-[10px] text-slate-500 border-t border-white/10">Bangalore · Private staff system</div>
    </div>
  );
}

function TabBar() {
  const groups = useGroups();
  const { pathname } = useLocation();
  const g = groups.find((x) => x.tabs.some((t) => matches(t.to, pathname)));
  if (!g || g.tabs.length < 2) return null;
  return (
    <div className="-mt-1 mb-5 flex gap-1 overflow-x-auto border-b print:hidden" data-testid="section-tabs">
      {g.tabs.map((t) => (
        <NavLink key={t.to} to={t.to} data-testid={`tab-${t.id}`}
          className={() => cn("whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors", matches(t.to, pathname) ? "border-red-600 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{t.label}</NavLink>
      ))}
    </div>
  );
}

function BottomNav({ onMore }) {
  const groups = useGroups();
  const { pathname } = useLocation();
  const pick = ["home", "cutting", "dispatch", "stock", "ai"].map((id) => groups.find((g) => g.id === id)).filter(Boolean).slice(0, 4);
  const cls = (a) => cn("flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors", a ? "text-red-600" : "text-muted-foreground");
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 h-16 border-t bg-background/95 backdrop-blur-md flex pb-[env(safe-area-inset-bottom)] print:hidden" data-testid="bottom-nav">
      {pick.map((g) => (
        <NavLink key={g.id} to={g.tabs[0].to} end={g.tabs[0].to === "/"} className={() => cls(g.tabs.some((t) => matches(t.to, pathname)))} data-testid={`bottom-nav-${g.id}`}>
          <g.icon className="h-5 w-5" />{{ home: "Home", cutting: "Cutting", dispatch: "Dispatch", stock: "Stock", ai: "Ask AI" }[g.id]}
        </NavLink>
      ))}
      <button type="button" onClick={onMore} className={cls(false)} data-testid="bottom-nav-more"><Menu className="h-5 w-5" />More</button>
    </nav>
  );
}

function useTableLabels(ref, key) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const label = () => el.querySelectorAll("table").forEach((t) => {
      const heads = [...t.querySelectorAll("thead th")].map((h) => h.textContent.trim());
      t.querySelectorAll("tbody tr").forEach((tr) => [...tr.children].forEach((td, i) => { if (td.dataset.label !== heads[i]) td.dataset.label = heads[i] || ""; }));
    });
    label();
    const mo = new MutationObserver(label);
    mo.observe(el, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [ref, key]);
}

export default function Layout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("roxy-theme") === "dark");
  const loc = useLocation();
  const nav = useNavigate();
  const mainRef = useRef(null);
  useTableLabels(mainRef, loc.pathname);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("roxy-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-40"><SideNav /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-64 border-0"><SideNav onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-14 border-b bg-background/80 backdrop-blur-md flex items-center gap-3 px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
          <div className="flex-1 text-sm text-muted-foreground hidden sm:block">Bangalore Main Warehouse</div>
          <div className="flex-1 sm:hidden font-heading font-bold">ROXY</div>
          {can("cutting:create") && <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white hidden sm:inline-flex" onClick={() => nav("/cutting")} data-testid="header-new-cutting-btn">
            <Scissors className="h-4 w-4 mr-1" /> Cable Cutting
          </Button>}
          <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle-btn">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <div className="text-right hidden md:block" data-testid="header-user-info">
            <div className="text-sm font-medium leading-tight">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground">{user?.role_label}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
        </header>
        <main ref={mainRef} className="rx-main p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 max-w-[1600px]"><TabBar /><Outlet /></main>
      </div>
      <BottomNav onMore={() => setOpen(true)} />
    </div>
  );
}
